import {t} from "../../../styles/theme/tokens";

/**
 * Браузерные API уведомлений: системное уведомление, разрешение, видимость
 * вкладки и короткий сигнал WebAudio (без файла-ассета).
 */

export const notificationsSupported = (): boolean => typeof window !== "undefined" && "Notification" in window;

/** Вкладка видна и окно в фокусе. */
export const isPageActive = (): boolean =>
    typeof document !== "undefined" && document.visibilityState === "visible" && document.hasFocus();

interface ShowOptions {
    title: string;
    body: string;
    tag: string;
    onClick: () => void;
}

/** Клики уведомлений, показанных через service worker: `tag → onClick` (S10). */
const swClicks = new Map<string, () => void>();
let swListening = false;

/** Один слушатель сообщений `notification-click` от `public/sw.js`. */
const listenSwClicks = (sw: ServiceWorkerContainer) => {
    if (swListening) return;
    swListening = true;
    sw.addEventListener("message", (e: MessageEvent) => {
        if (e.data?.type !== "notification-click") return;
        const tag = e.data.data?.tag as string | undefined;
        const onClick = tag ? swClicks.get(tag) : undefined;
        if (!tag || !onClick) return;
        swClicks.delete(tag);
        onClick();
    });
};

/** Регистрация SW, если она готова за ~1 с; иначе null — показ прежним путём. */
const swRegistration = (sw: ServiceWorkerContainer): Promise<ServiceWorkerRegistration | null> =>
    Promise.race([sw.ready, new Promise<null>((resolve) => setTimeout(() => resolve(null), 1000))]).catch(() => null);

/** `data` для клика: тег и то, на что он указывает (`chat:<channel>` → channel, `<type>:<carId>` → carId). */
const clickData = (tag: string) => {
    const id = tag.slice(tag.indexOf(":") + 1);
    return tag.startsWith("chat:") ? {tag, channel: id} : {tag, carId: id};
};

/**
 * Системное уведомление, если разрешено. Есть готовый service worker —
 * через него (Android Chrome умеет только так), клик приходит сообщением;
 * нет SW — `new Notification` синхронно, как до S10. Ошибка показа — молча ничего.
 */
export const showSystemNotification = async ({title, body, tag, onClick}: ShowOptions): Promise<void> => {
    if (!notificationsSupported() || Notification.permission !== "granted") return;
    const sw = typeof navigator !== "undefined" ? navigator.serviceWorker : undefined;
    const registration = sw ? await swRegistration(sw) : null;
    if (sw && registration) {
        listenSwClicks(sw);
        swClicks.set(tag, onClick);
        try {
            await registration.showNotification(title, {body, tag, data: clickData(tag), renotify: true});
        } catch {
            swClicks.delete(tag);
        }
        return;
    }
    try {
        const n = new Notification(title, {body, tag});
        n.onclick = () => {
            window.focus();
            onClick();
            n.close();
        };
    } catch {
        // Android Chrome без service worker: конструктор бросает исключение.
    }
};

// ─── Звук алерта ───────────────────────────────────────────────────────────

let audio: AudioContext | null = null;

const context = (): AudioContext | null => {
    if (audio) return audio;
    const Ctor = window.AudioContext ?? (window as unknown as {webkitAudioContext?: typeof AudioContext}).webkitAudioContext;
    if (!Ctor) return null;
    try {
        audio = new Ctor();
    } catch {
        return null;
    }
    return audio;
};

/**
 * Браузер разрешает звук только после жеста пользователя: первый клик или
 * клавиша на странице «будит» AudioContext. Возвращает снятие слушателей.
 */
export const unlockAudioOnGesture = (): (() => void) => {
    const off = () => {
        window.removeEventListener("pointerdown", wake);
        window.removeEventListener("keydown", wake);
    };
    const wake = () => {
        const ctx = context();
        if (ctx && ctx.state === "suspended") void ctx.resume();
        off();
    };
    window.addEventListener("pointerdown", wake);
    window.addEventListener("keydown", wake);
    return off;
};

/** Короткий негромкий двухтональный сигнал ~0,5 с. */
export const playAlertBeep = () => {
    const ctx = context();
    if (!ctx) return;
    if (ctx.state === "suspended") void ctx.resume();
    const start = ctx.currentTime + 0.01;
    const gain = ctx.createGain();
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.12, start + 0.03);
    gain.gain.setValueAtTime(0.12, start + 0.42);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.5);
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, start);
    osc.frequency.setValueAtTime(660, start + 0.25);
    osc.connect(gain);
    osc.start(start);
    osc.stop(start + 0.52);
};

/** «Машина пришла на наш этап» (S4.2): один тон ниже и тише алертного, ~0,2 с. */
export const playArrivalBeep = () => {
    const ctx = context();
    if (!ctx) return;
    if (ctx.state === "suspended") void ctx.resume();
    const start = ctx.currentTime + 0.01;
    const gain = ctx.createGain();
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.05, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.2);
    const osc = ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(523, start);
    osc.connect(gain);
    osc.start(start);
    osc.stop(start + 0.22);
};

// ─── Значок вкладки с красной точкой ────────────────────────────────────────

let originalIcon: string | null = null;
/** Значок с точкой — по цвету: у светлой и тёмной темы error.main разный. */
const dotIcons = new Map<string, Promise<string | null>>();
/** Последний запрос: «включить», дорисованный после «снять», не должен вернуть точку. */
let wantDot = false;

/**
 * Canvas не понимает `var(--…)`: цвет токена `t.error.main` вычисляется на
 * пробном элементе. Не вычислился — точки нет (null).
 */
const dotColor = (): string | null => {
    const probe = document.createElement("span");
    probe.style.color = t.error.main;
    document.body.appendChild(probe);
    const color = getComputedStyle(probe).color;
    probe.remove();
    return color || null;
};

const iconLink = (): HTMLLinkElement | null => document.querySelector<HTMLLinkElement>("link[rel~='icon']");

/** Рисует исходный значок и красную точку справа сверху; без исходного — только точку. */
const buildDotIcon = (src: string, color: string): Promise<string | null> =>
    new Promise((resolve) => {
        const draw = (img: HTMLImageElement | null) => {
            try {
                const canvas = document.createElement("canvas");
                canvas.width = 32;
                canvas.height = 32;
                const g = canvas.getContext("2d");
                if (!g) return resolve(null);
                if (img) g.drawImage(img, 0, 0, 32, 32);
                g.beginPath();
                g.arc(24, 8, 7, 0, Math.PI * 2);
                g.fillStyle = color;
                g.fill();
                resolve(canvas.toDataURL("image/png"));
            } catch {
                resolve(null);
            }
        };
        const img = new Image();
        img.onload = () => draw(img);
        img.onerror = () => draw(null);
        img.src = src;
    });

/** Красная точка на значке вкладки: включить / снять (исходный href возвращается). */
export const setFaviconDot = async (on: boolean): Promise<void> => {
    if (typeof document === "undefined") return;
    const link = iconLink();
    if (!link) return;
    if (originalIcon === null) originalIcon = link.href;
    wantDot = on;
    if (!on) {
        link.href = originalIcon;
        return;
    }
    const color = dotColor();
    if (!color) return;
    if (!dotIcons.has(color)) dotIcons.set(color, buildDotIcon(originalIcon, color));
    const icon = await dotIcons.get(color);
    if (icon && wantDot) link.href = icon;
};
